class SoarHeader extends HTMLElement {
  connectedCallback() {
    this.sticky = this.dataset.sticky === 'true';
    this.hideOnScroll = this.dataset.hideOnScroll === 'true';

    this.bindMenuTriggers();
    this.bindMobileDrawer();
    this.bindDrawerAccordions();
    this.bindCartCountUpdates();
    this.trackMegaTop();
    if (this.hideOnScroll && this.sticky) this.bindHideOnScroll();
  }

  trackMegaTop() {
    const update = () => {
      const rect = this.getBoundingClientRect();
      this.style.setProperty('--soar-mega-top', `${Math.max(0, rect.bottom)}px`);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  bindMenuTriggers() {
    const items = this.querySelectorAll('.soar-header__menu-item[data-has-children="true"]');
    const CLOSE_DELAY = 320;

    items.forEach((item) => {
      const trigger = item.querySelector('.soar-header__menu-link');
      if (!trigger) return;

      const mega = item.querySelector('.soar-mega-menu, .soar-header__dropdown');
      let closeTimer = null;
      const cancelClose = () => { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } };

      const open = () => {
        cancelClose();
        items.forEach((other) => {
          if (other !== item) {
            other.dataset.open = 'false';
            const t = other.querySelector('.soar-header__menu-link');
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
        item.dataset.open = 'true';
        trigger.setAttribute('aria-expanded', 'true');
        // Position the hover bridge under THIS trigger only, so it never
        // covers (and blocks clicks on) the other nav links.
        if (mega) {
          const r = trigger.getBoundingClientRect();
          mega.style.setProperty('--soar-mega-bridge-left', `${r.left}px`);
          mega.style.setProperty('--soar-mega-bridge-width', `${r.width}px`);
        }
      };
      const close = () => {
        item.dataset.open = 'false';
        trigger.setAttribute('aria-expanded', 'false');
      };
      const scheduleClose = () => {
        cancelClose();
        closeTimer = setTimeout(close, CLOSE_DELAY);
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', scheduleClose);

      if (mega) {
        mega.addEventListener('mouseenter', cancelClose);
        mega.addEventListener('mouseleave', scheduleClose);
      }

      trigger.addEventListener('focus', open);
      trigger.addEventListener('click', (e) => {
        if (item.dataset.open === 'true') {
          close();
        } else {
          e.preventDefault();
          open();
        }
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          close();
          trigger.focus();
        }
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget) && (!mega || !mega.contains(e.relatedTarget))) close();
      });
    });

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        items.forEach((item) => {
          item.dataset.open = 'false';
          const trigger = item.querySelector('.soar-header__menu-link');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  bindMobileDrawer() {
    const toggle = this.querySelector('.soar-header__mobile-toggle');
    const close = this.querySelector('.soar-header__drawer-close');
    const overlay = this.querySelector('.soar-header__drawer-overlay');
    if (!toggle) return;

    const setOpen = (open) => {
      this.dataset.drawerOpen = open ? 'true' : 'false';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
      if (!open && typeof this.resetDrawerPanels === 'function') {
        // Wait for the drawer slide-out to finish before snapping back to root.
        setTimeout(() => this.resetDrawerPanels(), 300);
      }
    };

    toggle.addEventListener('click', () => setOpen(this.dataset.drawerOpen !== 'true'));
    if (close) close.addEventListener('click', () => setOpen(false));
    if (overlay) overlay.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dataset.drawerOpen === 'true') setOpen(false);
    });
  }

  bindDrawerAccordions() {
    const root = this.querySelector('[data-drawer-panels]');
    if (!root) return;

    const showPanel = (id) => {
      const panel = root.querySelector(`.soar-header__panel[data-panel="${id}"]`);
      if (panel) panel.classList.add('is-active');
    };

    root.querySelectorAll('[data-panel-open]').forEach((btn) => {
      btn.addEventListener('click', () => showPanel(btn.dataset.panelOpen));
    });

    // Back: slide the current panel out; its parent is still visible underneath.
    root.querySelectorAll('[data-panel-back]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('.soar-header__panel');
        if (panel) panel.classList.remove('is-active');
      });
    });

    // Reset to the root panel whenever the drawer closes.
    this.resetDrawerPanels = () => {
      root.querySelectorAll('.soar-header__panel').forEach((p) => {
        if (p.dataset.panel !== 'root') p.classList.remove('is-active');
      });
    };
  }

  bindCartCountUpdates() {
    document.addEventListener('cart:updated', (e) => {
      const count = (e.detail && e.detail.itemCount) ?? null;
      if (count === null) return;
      this.querySelectorAll('[data-soar-cart-count]').forEach((el) => {
        el.textContent = count;
        el.dataset.empty = count === 0 ? 'true' : 'false';
      });
    });
  }

  bindHideOnScroll() {
    let lastY = window.scrollY;
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const goingDown = y > lastY && y > 80;
        this.dataset.hidden = goingDown ? 'true' : 'false';
        lastY = y;
        ticking = false;
      });
      ticking = true;
    }, { passive: true });
  }
}

if (!customElements.get('soar-header')) {
  customElements.define('soar-header', SoarHeader);
}
